import React, { useState, useEffect } from 'react';
import SearchDataContext from './searchData-context';

import { field } from '../types/Field';
import SearchService from '../services/SearchService';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
    children: React.ReactNode;
}

const SearchDataProvider = (props: Props) => {
    // const [state, setState] = useState<string>('');
    // const [county, setCounty] = useState<string>('');
    // const [countyName, setCountyName] = useState<string>('');
    // const [format, setFormat] = useState<string>('');
    // const [formatDescription, setFormatDescription] = useState<string>('');
    // const [searchBy, setSearchBy] = useState<string>('');
    // const [mode, setMode] = useState<string>('BackPlant');

    const [searchData, setSearchData] = useState({
        state: '',
        countyId: '',
        countyName: '',
        countySupportedPlatform: '',
        format: '',
        formatDescription: '',
        searchBy: '',
        searchByDescription: '',
        isReverse: false,
        coverageStart: '',
        coverageEnd: '',
        fields: [] as field[],
        searchMessage: '',
    });

    const { authState } = useOktaAuth();

    const userName: string = authState?.accessToken?.claims?.sub;
    const cid: string = authState?.accessToken?.claims?.cid;
    const accessToken: string = authState?.accessToken?.accessToken;

    useEffect(() => {
        if (!userName) {
            return;
        }

        if (!searchData.format || !searchData.searchBy) {
            setSearchData((prevState) => {
                return { ...prevState, fields: [] as field[] };
            });
        } else {
            SearchService.getSearchFieldList(
                searchData.countyId,
                searchData.searchBy,
                searchData.format,
                accessToken,
                cid
            )
                .then((response) => {
                    setSearchData((prevState) => {
                        return { ...prevState, fields: response };
                    });
                })
                .catch((ex) => {
                    // TM: what if we can't get the field list?
                    return;
                });
        }
    }, [authState, searchData.format, searchData.searchBy]);

    // reset everything except mode when state is changed.
    const setStateHandler = (newState: string) => {
        setSearchData((prevState) => {
            return {
                ...prevState,
                state: newState,
                countyId: '',
                countyName: '',
                countySupportedPlatform: '',
                format: '',
                formatDescription: '',
                searchBy: '',
                searchByDescription: '',
                coverageStart: '',
                coverageEnd: '',
                fields: [] as field[],
                searchMessage: '',
            };
        });
    };

    const setCountyHandler = (
        newCountyId: string,
        newCountyName: string,
        newCountySupportedPlatform: string
    ) => {
        setSearchData((prevState) => {
            return {
                ...prevState,
                countyId: newCountyId,
                countyName: newCountyName,
                countySupportedPlatform: newCountySupportedPlatform,
                format: '',
                formatDescription: '',
                searchBy: '',
                searchByDescription: '',
                coverageStart: '',
                coverageEnd: '',
                fields: [] as field[],
                searchMessage: '',
            };
        });
    };

    const setFormatHandler = async (newFormat: string, newFormatDescription: string) => {
        setSearchData((prevState) => {
            let newState = {
                ...prevState,
                format: newFormat,
                formatDescription: newFormatDescription,
                coverageStart: '',
                coverageEnd: '',
                searchMessage: '',
            };
            if (!prevState.isReverse) {
                newState.searchBy = '';
                newState.searchByDescription = '';
                newState.fields = [] as field[];
            }
            return newState;
        });
    };

    const setSearchByHandler = (newSearchBy: string, newSearchByDescription: string) => {
        setSearchData((prevState) => {
            let newState = {
                ...prevState,
                searchBy: newSearchBy,
                searchByDescription: newSearchByDescription,
                coverageStart: '',
                coverageEnd: '',
                fields: [] as field[],
                searchMessage: '',
            };
            if (prevState.isReverse) {
                newState.format = '';
                newState.formatDescription = '';
            }
            return newState;
        });
    };

    const setCoverageHandler = (start: string, end: string) => {
        setSearchData((prevState) => {
            return {
                ...prevState,
                coverageStart: start,
                coverageEnd: end,
            };
        });
    };

    const setFieldsHandler = (data: field[]) => {
        setSearchData((prevState) => {
            return {
                ...prevState,
                fields: data,
            };
        });
    };

    const setFieldValueHandler = (abbreviation: string, newValue: string) => {
        setSearchData((prevState) => {
            let newFieldsData = prevState.fields;
            newFieldsData = prevState.fields?.map((oldValue: field) => {
                if (oldValue.abbreviation == abbreviation) {
                    return { ...oldValue, value: newValue };
                }
                return oldValue;
            });
            return {
                ...prevState,
                fields: newFieldsData,
                searchMessage: '',
            };
        });
    };

    // when mode changes, reset format and search by
    const setIsReverseHandler = (newIsReverse: boolean) => {
        setSearchData((prevState) => {
            return {
                ...prevState,
                isReverse: newIsReverse,
                format: '',
                formatDescription: '',
                searchBy: '',
                searchByDescription: '',
                fields: [] as field[],
                searchMessage: '',
            };
        });
    };

    const setSearchMessageHandler = (newSearchMessage: string) => {
        setSearchData((prevState) => {
            return {
                ...prevState,
                searchMessage: newSearchMessage,
            };
        });
    };

    const searchDataContext = {
        state: searchData.state,
        countyId: searchData.countyId,
        countyName: searchData.countyName,
        countySupportedPlatform: searchData.countySupportedPlatform,
        format: searchData.format,
        formatDescription: searchData.formatDescription,
        searchBy: searchData.searchBy,
        searchByDescription: searchData.searchByDescription,
        isReverse: searchData.isReverse,
        coverageStart: searchData.coverageStart,
        coverageEnd: searchData.coverageEnd,
        fields: searchData.fields as field[],
        searchMessage: searchData.searchMessage,
        setState: setStateHandler,
        setCounty: setCountyHandler,
        setFormat: setFormatHandler,
        setSearchBy: setSearchByHandler,
        setIsReverse: setIsReverseHandler,
        setCoverage: setCoverageHandler,
        setFields: setFieldsHandler,
        setFieldValue: setFieldValueHandler,
        setSearchMessage: setSearchMessageHandler,
    };

    return (
        <SearchDataContext.Provider value={searchDataContext}>
            {props.children}
        </SearchDataContext.Provider>
    );
};

export default SearchDataProvider;
